import { Component, Input, OnDestroy, OnInit, Type, ViewEncapsulation } from '@angular/core';
import { GaWizardService } from '../../../shared/wizard/ga-wizard.service';
import { GaWizard, IWizardStep } from '../../../shared/wizard/ga-wizard';
import { WIZARD_BP_WIZARD } from '../../../shared/app-wizards';
import { STATE_BPPROFILES_GENERAL, STATE_BPWIZARD_AGREEMENT, STATE_BPWIZARD_BANK, STATE_BPWIZARD_BILLINGMETHODS, STATE_BPWIZARD_COMPANYMARGIN, STATE_BPWIZARD_FEES, STATE_BPWIZARD_GENERAL, STATE_BPWIZARD_OEM, STATE_BPWIZARD_PAYMENTSOURCE, STATE_BPWIZARD_PAYMENTTERMS } from '../../../shared/app-states';
import { StateService, Transition, TransitionService } from '@uirouter/angular';
import { BPProfilesService } from '../bp-profiles.service';
import { TranslationService } from '../../../shared/translation/translation.service';
import { BPProfilesOEMDataService } from '../advanced/oem/bp-profiles-oem-data.service';
import { BPWizardAction, BPWizardService, BPWizardStep } from './bp-wizard.service';
import { BPWizardGeneralComponent } from './general/bp-wizard-general.component';
import { BPWizardOEMComponent } from './oem/bp-wizard-oem.component';
import { BPWizardCompanyMarginComponent } from './company-margin/bp-wizard-company-margin.component';
import { BPWizardPaymentTermsComponent } from './payment-terms/bp-wizard-payment-terms.component';
import { BPWizardBillingMethodsComponent } from './billing-methods/bp-wizard-billing-methods.component';
import { BPWizardPaymentSourceComponent } from './payment-source/bp-wizard-payment-source.component';
import { BPWizardBankComponent } from './bank/bp-wizard-bank.component';
import { BPWizardFeesComponent } from './fees/bp-wizard-fees.component';
import { BPWizardAgreementComponent } from './agreement/bp-wizard-agreement.component';
import { Subscription } from 'rxjs';
import { IBPWizardProgress } from '../bp-profiles-data.service';

@Component({
  selector: 'bp-wizard',
  template: `
    <h2 *ngIf="loading"><activity-indicator [useDefaultText]="true"></activity-indicator></h2>

    <div *ngIf="!loading">
      <wizard-wrapper 
        [loading]="loading" 
        [error]="error" 
        [wizardId]="wizardId"
        [showProgress]="true"
        [progress]="progress"
        [completedSteps]="completedSteps"
        [navigationDisabled]="wizardNavigationDisabled"
        wizardTitle="BPWIZARD.TITLE"></wizard-wrapper>
    </div>
  `,
  styleUrls: ['./bp-wizard.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BPWizardComponent implements OnInit, OnDestroy {
  @Input() public profileId:number;
  public wizardId:string = WIZARD_BP_WIZARD;
  public loading:boolean;
  public progress:number;
  public completedSteps:string[];
  public wizardNavigationDisabled:boolean;
  public error:string;

  private wizard:GaWizard;
  private onStateSuccessHandler:Function;
  private onProgressChangeSubscription:Subscription;
  private onUpdateWizardButtonStatusSubscription:Subscription;
  private onSaveButtonEnabledChangeSubscription:Subscription;

  constructor(
    private wizardService:GaWizardService,
    private stateService:StateService,
    private bpProfilesService:BPProfilesService,
    private translationService:TranslationService,
    private bpProfilesOEMDataService:BPProfilesOEMDataService,
    private bpWizardService:BPWizardService,
    private transitionService:TransitionService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.wizard = this.wizardService.getInstance(this.wizardId);

    this.loading = true;
    this.bpWizardService.initBPWizardData(this.profileId)
      .then(this.initWizard)
      .finally(() => {
        this.loading = false;
      });

    // register listener
    this.listen();
  }

  private listen():void {
    // when state change successfully then we need to reload translation modules
    this.onStateSuccessHandler = this.transitionService.onSuccess({}, (transition:Transition) => {
      const isLastStep:boolean = transition.to().name === STATE_BPWIZARD_AGREEMENT;
      this.wizard.setActionVisible(BPWizardAction.SAVE, !isLastStep);
      this.wizard.setActionVisible(BPWizardAction.SUBMIT, isLastStep);
    });

    this.onProgressChangeSubscription = this.bpWizardService.onProgressChange.subscribe((wizardProgress:IBPWizardProgress) => {
      this.progress = wizardProgress?.progress;
      this.completedSteps = wizardProgress?.completedTabs;
    });

    this.onUpdateWizardButtonStatusSubscription = this.bpWizardService.onUpdateWizardButtonStatus.subscribe(this.updateButtonStatus);
    this.onSaveButtonEnabledChangeSubscription = this.bpWizardService.onSaveButtonEnabledChange.subscribe(this.onSaveButtonEnabledChange);
  }

  private initWizard = ():void => {
    this.createWizardSteps();
    this.createWizardActions();
  };

  private createWizardSteps():void {
    const createWizardStep = (id:string, wizardMenuTitle:string, component:Type<any>, stateName:string):IWizardStep => {
      return this.wizardService.createStep(id, wizardMenuTitle, component, stateName, undefined, undefined, true);
    };

    this.wizard.steps([
      createWizardStep(BPWizardStep.GENERAL, 'BPWIZARD.GENERAL.HEADING.TITLE', BPWizardGeneralComponent, STATE_BPWIZARD_GENERAL),
      createWizardStep(BPWizardStep.OEM, 'BPWIZARD.OEM.HEADING.TITLE', BPWizardOEMComponent, STATE_BPWIZARD_OEM),
      createWizardStep(BPWizardStep.COMPANY_MARGIN, 'BPWIZARD.COMPANY_MARGIN.HEADING.TITLE', BPWizardCompanyMarginComponent, STATE_BPWIZARD_COMPANYMARGIN),
      createWizardStep(BPWizardStep.PAYMENT_TERMS, 'BPWIZARD.PAYMENT_TERMS.HEADING.TITLE', BPWizardPaymentTermsComponent, STATE_BPWIZARD_PAYMENTTERMS),
      createWizardStep(BPWizardStep.BILLING_METHODS, 'BPWIZARD.BILLING_METHODS.HEADING.TITLE', BPWizardBillingMethodsComponent, STATE_BPWIZARD_BILLINGMETHODS),
      createWizardStep(BPWizardStep.PAYMENT_SOURCE, 'BPWIZARD.PAYMENT_SOURCE.HEADING.TITLE', BPWizardPaymentSourceComponent, STATE_BPWIZARD_PAYMENTSOURCE),
      createWizardStep(BPWizardStep.BANK, 'BPWIZARD.BANK_DETAILS.HEADING.TITLE', BPWizardBankComponent, STATE_BPWIZARD_BANK),
      createWizardStep(BPWizardStep.FEES, 'BPWIZARD.FEES.HEADING.TITLE', BPWizardFeesComponent, STATE_BPWIZARD_FEES),
      createWizardStep(BPWizardStep.AGREEMENT, 'BPWIZARD.AGREEMENT.HEADING.TITLE', BPWizardAgreementComponent, STATE_BPWIZARD_AGREEMENT)
    ]);

    this.wizard.sync();
  }

  private createWizardActions():void {
    this.wizard.actions([
      this.wizardService.createActionButton(BPWizardAction.SWITCH_TO_ADVANCED, this.translationService.instant('BPWIZARD.BUTTONS.ADVANCED'), 'advanced-switch', this.onSwitchToAdvanced),
      this.wizardService.createActionButton(BPWizardAction.SAVE, this.translationService.instant('GA.BUTTONS.SAVE'), 'btn-success', this.onSave, this.stateService.current.name === STATE_BPWIZARD_AGREEMENT, true),
      this.wizardService.createActionButton(BPWizardAction.SUBMIT, this.translationService.instant('GA.BUTTONS.SUBMIT'), 'btn-success', this.onSave.bind(this, true), this.stateService.current.name !== STATE_BPWIZARD_AGREEMENT)
    ]);

    this.wizard.setActionEnabled(BPWizardAction.SAVE, false);
  }

  private updateButtonStatus = (isBusy:boolean):void => {
    this.wizardNavigationDisabled = isBusy;
    this.wizard.setActionBusy(BPWizardAction.SAVE, isBusy);
    this.wizard.setActionBusy(BPWizardAction.SUBMIT, isBusy);
    if (isBusy) {
      this.wizard.setActionEnabled(BPWizardAction.SAVE, false);
    }
    this.wizard.setActionEnabled(BPWizardAction.SUBMIT, !isBusy);
  };

  private onSaveButtonEnabledChange = (isEnabled:boolean):void => {
    this.wizard.setActionEnabled(BPWizardAction.SAVE, isEnabled);
  };

  private onSwitchToAdvanced = ():void => {
    const advancedState:string = this.stateService.current.data?.advancedState || STATE_BPPROFILES_GENERAL;
    this.stateService.go(advancedState, { profileListType: 'bpprofiles', profileId: this.bpProfilesService.profileDetail?.id });
  };

  private onSave = (isSubmit?:boolean):void => {
    this.bpWizardService.emitSave(isSubmit);
  };

  public ngOnDestroy():void {
    this.wizard.clear();
    this.bpWizardService.clear();
    this.bpProfilesOEMDataService.clear();
    this.onStateSuccessHandler();
    this.onProgressChangeSubscription?.unsubscribe();
    this.onUpdateWizardButtonStatusSubscription?.unsubscribe();
    this.onSaveButtonEnabledChangeSubscription?.unsubscribe();
  }
}